import type React from 'react';
import { TREE_CARD_BACKGROUND } from './treeRowLook';

// IL TRASCINAMENTO DELLE RIGHE, UNO SOLO PER TUTTI GLI ELENCHI (utente, 2026-10-15; prototipo V2,
// `startOrderDrag` in app.js). Prima ogni elenco usava il drag nativo del browser: un'immagine
// fantasma che il sistema disegna come vuole, nessuna linea che dica dove cadrà la riga, e un
// bersaglio deciso dall'elemento sotto il puntatore — cioè spesso dal figlio sbagliato.
//
// Qui il gesto è del puntatore e lo disegniamo noi:
// - la COPIA della riga segue la mano senza scatti, con «+N» quando se ne portano più d'una;
// - le righe di partenza restano al loro posto, vuote, così l'elenco non salta;
// - una LINEA con un pallino dice dove cadrà, oppure la riga che la accoglierà dentro si accende;
// - vicino ai bordi l'elenco scorre da solo; Esc annulla.
//
// L'elenco dichiara le sue righe nel DOM — `data-reorder-id` e `data-reorder-level` (la profondità,
// che dice quali righe seguono una riga come suo ramo) — e chi lo ospita decide le regole con
// `canDrop`: questo file non sa che cosa sia un attributo o una mesh, sa solo dove sta il puntatore.

export type ReorderPosition = 'before' | 'after' | 'into';

export interface PointerReorderOptions {
    /** La riga presa, per id. */
    draggedId: string;
    /**
     * TUTTE LE RIGHE CHE PARTONO (testate), nell'ordine dell'elenco: la sola presa, oppure la selezione
     * della stessa specie quando la riga presa ne fa parte. Ognuna si porta dietro il suo ramo.
     */
    carriedIds: string[];
    /** Si può lasciare lì? Chiamato con le righe dell'elenco che non partono. */
    canDrop: (targetId: string, position: ReorderPosition) => boolean;
    onDrop: (targetId: string, position: ReorderPosition) => void;
    /** Il gesto è partito (dopo qualche pixel): chi ospita può chiudere menu o rinomine. */
    onStart?: () => void;
    /** Il gesto è finito, lasciato o annullato: dopo `onDrop`. */
    onEnd?: () => void;
    /**
     * SI PRENDE LA RIGA INTERA, NON UNA MANIGLIA (utente, 2026-10-15): in un outliner la maniglia occupa una
     * colonna per dire una cosa che la riga può dire da sola. Il gesto parte da qualunque punto della riga che
     * non sia un comando suo — la freccia, l'occhio, il «…», il campo del nome —, e finché il puntatore non si
     * sposta di qualche pixel resta un clic: scegliere, aprire, rinominare funzionano come prima.
     */
    fromRow?: boolean;
}

/** I comandi dentro una riga: da lì non parte un trascinamento, parte il loro clic. */
const INTERACTIVE = 'button, input, textarea, select, a, label, [role="switch"], [role="checkbox"], [contenteditable="true"]';

const ROW = '[data-reorder-id]';
const START_DISTANCE = 4;
const EDGE = 28;

const STYLE_ID = 'zk-pointer-reorder';
const ensureStyles = () => {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    // I colori arrivano da `--reorder-accent` sul contenitore dell'elenco (il tema del marchio), poi da
    // `--composer-accent`, con l'arancio di Zakeke come ripiego. `!important` perché le righe hanno fondi e anelli loro.
    // Mentre si trascina i suggerimenti si nascondono (2026-10-15): quello della presa restava acceso sulla mano.
    style.textContent = `
        .zk-reordering, .zk-reordering * { cursor: grabbing !important; user-select: none !important; }
        .zk-reordering [data-ztooltip] { display: none !important; }
        .zk-reorder-src { background: ${TREE_CARD_BACKGROUND} !important; box-shadow: inset 0 0 0 1px var(--editor-line, #d1d3d8) !important; }
        .zk-reorder-src > * { visibility: hidden !important; }
        .zk-reorder-host { background: color-mix(in srgb, var(--zk-reorder-accent) 10%, #fff) !important; box-shadow: inset 0 0 0 1.5px var(--zk-reorder-accent) !important; }
        .zk-reorder-ghost { position: fixed; top: 0; left: 0; z-index: 4000; pointer-events: none; border-radius: 8px; background: #fff;
            box-shadow: 0 10px 28px rgba(38, 50, 56, .18), 0 2px 6px rgba(38, 50, 56, .12); will-change: transform; overflow: visible; }
        .zk-reorder-ghost > [data-reorder-id] { margin: 0 !important; width: 100% !important; background: transparent !important; box-shadow: none !important; }
        .zk-reorder-count { position: absolute; top: -8px; right: -8px; padding: 0 6px; border-radius: 999px; background: var(--zk-reorder-accent);
            color: #fff; font-size: 10px; font-weight: 600; line-height: 16px; }
        .zk-reorder-line { position: fixed; top: 0; left: 0; z-index: 4001; height: 2px; border-radius: 999px; background: var(--zk-reorder-accent);
            pointer-events: none; }
        .zk-reorder-line::before { content: ''; position: absolute; left: -4px; top: -3px; width: 8px; height: 8px; border-radius: 50%;
            background: #fff; box-shadow: inset 0 0 0 2px var(--zk-reorder-accent); }
    `;
    document.head.appendChild(style);
};

const levelOf = (row: HTMLElement) => Number(row.dataset.reorderLevel ?? 0);
const idOf = (row: HTMLElement) => row.dataset.reorderId ?? '';

/** Il ramo di una riga: lei e le righe che la seguono più in profondità. */
const blockOf = (rows: HTMLElement[], row: HTMLElement) => {
    const start = rows.indexOf(row);
    const level = levelOf(row);
    const block = [row];
    for (let index = start + 1; index < rows.length && levelOf(rows[index]) > level; index++) block.push(rows[index]);
    return block;
};

const scrollerOf = (element: HTMLElement | null) => {
    for (let node = element; node && node !== document.body; node = node.parentElement) {
        const overflow = getComputedStyle(node).overflowY;
        if ((overflow === 'auto' || overflow === 'scroll') && node.scrollHeight > node.clientHeight) return node;
    }
    return null;
};

/**
 * DA DOVE PARTE LA LINEA: dal glifo della riga, non dal suo bordo, così la linea dice anche la
 * profondità a cui la riga cadrà. Chi vuole indicarlo lo marca con `data-reorder-anchor`.
 */
const startXOf = (row: HTMLElement) => {
    const anchor = row.querySelector<HTMLElement>('[data-reorder-anchor]') ?? row.querySelector<HTMLElement>('svg, img');
    return (anchor ?? row).getBoundingClientRect().left;
};

/**
 * Comincia il trascinamento dalla presa. Va chiamato su `onPointerDown` della presa: finché il
 * puntatore non si sposta di qualche pixel è ancora un clic, e il clic resta della presa.
 */
export const startPointerReorder = (event: React.PointerEvent<HTMLElement>, options: PointerReorderOptions) => {
    if (event.button !== 0) return;
    const handle = event.currentTarget;
    if (options.fromRow) {
        const hit = (event.target as HTMLElement).closest(INTERACTIVE);
        if (hit && hit !== handle && handle.contains(hit)) return;
    }
    const row = handle.closest<HTMLElement>(ROW);
    const scope = row?.parentElement?.closest<HTMLElement>('[data-reorder-scope]');
    if (!row || !scope) return;

    const originX = event.clientX;
    const originY = event.clientY;
    let lastX = originX;
    let lastY = originY;
    let started = false;
    let raf = 0;
    let drop: { target: HTMLElement; position: ReorderPosition } | null = null;

    let rows: HTMLElement[] = [];
    let carried: HTMLElement[] = [];
    let ghost: HTMLDivElement | null = null;
    let line: HTMLDivElement | null = null;
    let offsetX = 0;
    let offsetY = 0;
    let scroller: HTMLElement | null = null;

    const begin = () => {
        started = true;
        ensureStyles();
        // Partito dalla riga, il primo tratto del gesto ha già cominciato a selezionare del testo: si toglie.
        if (options.fromRow) window.getSelection()?.removeAllRanges();
        options.onStart?.();
        const scopeStyle = getComputedStyle(scope);
        const accent = scopeStyle.getPropertyValue('--reorder-accent').trim() || scopeStyle.getPropertyValue('--composer-accent').trim() || '#e8650a';
        document.documentElement.style.setProperty('--zk-reorder-accent', accent);

        const all = Array.from(scope.querySelectorAll<HTMLElement>(ROW));
        const heads = all.filter((candidate) => options.carriedIds.includes(idOf(candidate)));
        if (!heads.includes(row)) heads.push(row);
        carried = [];
        heads.forEach((head) => blockOf(all, head).forEach((member) => !carried.includes(member) && carried.push(member)));
        rows = all.filter((candidate) => !carried.includes(candidate));

        const rect = row.getBoundingClientRect();
        offsetX = originX - rect.left;
        offsetY = originY - rect.top;
        scroller = scrollerOf(scope);

        scope.classList.add('zk-reordering');
        document.body.classList.add('zk-reordering');
        carried.forEach((member) => member.classList.add('zk-reorder-src'));

        ghost = document.createElement('div');
        ghost.className = 'zk-reorder-ghost';
        ghost.style.width = `${Math.round(rect.width)}px`;
        const copy = row.cloneNode(true) as HTMLElement;
        copy.classList.remove('zk-reorder-src');
        copy.removeAttribute('id');
        ghost.appendChild(copy);
        if (heads.length > 1) {
            const count = document.createElement('span');
            count.className = 'zk-reorder-count';
            count.textContent = `+${heads.length - 1}`;
            ghost.appendChild(count);
        }
        document.body.appendChild(ghost);

        line = document.createElement('div');
        line.className = 'zk-reorder-line';
        line.hidden = true;
        document.body.appendChild(line);
    };

    const bottomOf = (target: HTMLElement) => {
        const block = blockOf(rows, target);
        return block[block.length - 1].getBoundingClientRect().bottom;
    };

    const hostOf = (target: HTMLElement) => {
        const level = levelOf(target);
        for (let index = rows.indexOf(target) - 1; index >= 0 && level; index--) {
            if (levelOf(rows[index]) < level) return rows[index];
        }
        return null;
    };

    // IL BERSAGLIO SOTTO IL PUNTATORE: davanti, dopo (con tutto il suo ramo) o dentro.
    const resolve = (y: number): typeof drop => {
        let over: HTMLElement | undefined;
        for (const candidate of rows) {
            if (y < candidate.getBoundingClientRect().bottom) {
                over = candidate;
                break;
            }
        }
        // SOTTO L'ULTIMA RIGA, nel vuoto dell'elenco, vale «in fondo al primo livello»: è da lì che si torna
        // alla radice, ora che uscire dall'elenco non vuol dire più niente.
        const last = rows[rows.length - 1];
        if (!over && last && levelOf(last) > 0) {
            let top: HTMLElement | null = last;
            while (top && levelOf(top) > 0) top = hostOf(top);
            if (top && options.canDrop(idOf(top), 'after')) return { target: top, position: 'after' };
        }
        over = over ?? last;
        if (!over) return null;
        const rect = over.getBoundingClientRect();
        const fraction = Math.max(0, Math.min(1, (y - rect.top) / (rect.height || 1)));
        const id = idOf(over);
        const canBefore = options.canDrop(id, 'before');
        const canAfter = options.canDrop(id, 'after');
        const canInto = options.canDrop(id, 'into');

        // Una riga che è sorella e ospite insieme: i bordi sono davanti e dopo, il centro è dentro.
        if (canInto && (canBefore || canAfter)) {
            const open = blockOf(rows, over).length > 1;
            if (fraction < 0.3 && canBefore) return { target: over, position: 'before' };
            if (fraction > 0.75 && !open && canAfter) return { target: over, position: 'after' };
            return { target: over, position: 'into' };
        }
        if (canBefore || canAfter) {
            if (canBefore && (fraction < 0.5 || !canAfter)) return { target: over, position: 'before' };
            return { target: over, position: 'after' };
        }
        if (canInto) {
            const previous = rows[rows.indexOf(over) - 1];
            if (fraction < 0.5 && previous && options.canDrop(idOf(previous), 'after')) return { target: previous, position: 'after' };
            return { target: over, position: 'into' };
        }
        // Sopra un figlio d'altra specie vale il suo antenato che può accoglierla accanto.
        for (let ancestor = hostOf(over); ancestor; ancestor = hostOf(ancestor)) {
            const ancestorId = idOf(ancestor);
            const before = options.canDrop(ancestorId, 'before');
            const after = options.canDrop(ancestorId, 'after');
            if (!before && !after) continue;
            const middle = (ancestor.getBoundingClientRect().top + bottomOf(ancestor)) / 2;
            if (before && (y < middle || !after)) return { target: ancestor, position: 'before' };
            return { target: ancestor, position: 'after' };
        }
        return null;
    };

    const paint = () => {
        if (!ghost || !line) return;
        ghost.style.transform = `translate(${Math.round(lastX - offsetX)}px, ${Math.round(lastY - offsetY)}px)`;
        drop = resolve(lastY);
        rows.forEach((candidate) => candidate.classList.toggle('zk-reorder-host', !!drop && drop.position === 'into' && candidate === drop.target));
        // Dentro un ospite non c'è linea: si accende l'ospite.
        if (!drop || drop.position === 'into') {
            line.hidden = true;
            return;
        }
        const scopeRect = scope.getBoundingClientRect();
        const targetRect = drop.target.getBoundingClientRect();
        const left = startXOf(drop.target);
        const y = drop.position === 'before' ? targetRect.top : bottomOf(drop.target);
        line.hidden = false;
        line.style.transform = `translate(${Math.round(left)}px, ${Math.round(y - 1)}px)`;
        line.style.width = `${Math.max(24, Math.round(scopeRect.right - 8 - left))}px`;
    };

    const tick = () => {
        raf = 0;
        if (scroller) {
            const rect = scroller.getBoundingClientRect();
            let velocity = 0;
            if (lastY < rect.top + EDGE) velocity = -Math.ceil((rect.top + EDGE - lastY) / 3);
            else if (lastY > rect.bottom - EDGE) velocity = Math.ceil((lastY - rect.bottom + EDGE) / 3);
            if (velocity) {
                scroller.scrollTop += velocity;
                raf = requestAnimationFrame(tick);
            }
        }
        paint();
    };

    const move = (moveEvent: PointerEvent) => {
        lastX = moveEvent.clientX;
        lastY = moveEvent.clientY;
        if (!started) {
            if (Math.hypot(lastX - originX, lastY - originY) < START_DISTANCE) return;
            begin();
        }
        if (!raf) raf = requestAnimationFrame(tick);
    };

    // IL CLIC CHE CHIUDE UN TRASCINAMENTO NON È UN CLIC: senza questo, lasciare la riga sulla sua
    // presa la sceglierebbe, o aprirebbe ciò che sta sotto il puntatore.
    const swallowNextClick = () => {
        const swallow = (clickEvent: MouseEvent) => {
            clickEvent.stopPropagation();
            clickEvent.preventDefault();
        };
        window.addEventListener('click', swallow, { capture: true, once: true });
        window.setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0);
    };

    const finish = (commit: boolean) => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', cancel);
        window.removeEventListener('keydown', key, true);
        if (raf) cancelAnimationFrame(raf);
        if (!started) return;
        ghost?.remove();
        line?.remove();
        scope.classList.remove('zk-reordering');
        document.body.classList.remove('zk-reordering');
        scope
            .querySelectorAll(`${ROW}.zk-reorder-src, ${ROW}.zk-reorder-host`)
            .forEach((member) => member.classList.remove('zk-reorder-src', 'zk-reorder-host'));
        swallowNextClick();
        // SI LASCIA DOVE DICE LA LINEA, anche col puntatore uscito dall'elenco (utente, 2026-10-15). Fuori
        // dalla colonna voleva dire «alla radice»: salendo verso la ricerca o scivolando di lato, due mesh
        // portate in cima al loro gruppo ne uscivano. Il prototipo non conosce il fuori, e nemmeno noi.
        if (commit && drop) options.onDrop(idOf(drop.target), drop.position);
        options.onEnd?.();
    };

    const up = () => finish(true);
    const cancel = () => finish(false);
    const key = (keyEvent: KeyboardEvent) => {
        if (keyEvent.key !== 'Escape' || !started) return;
        keyEvent.preventDefault();
        keyEvent.stopPropagation();
        finish(false);
    };

    // Dalla maniglia il gesto è solo un trascinamento; dalla riga è anche un clic, e la riga deve poter
    // prendere il fuoco e rispondere al doppio clic: lì non si ferma niente finché il gesto non parte.
    if (!options.fromRow) event.preventDefault();
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('keydown', key, true);
};

/**
 * IL NUOVO ORDINE DI UN ELENCO PIATTO: le righe portate escono dal loro posto e rientrano, nel loro
 * ordine, davanti o dopo il bersaglio. Restituisce gli id nell'ordine nuovo.
 */
export const reorderIds = (ordered: string[], carriedIds: string[], targetId: string, position: Exclude<ReorderPosition, 'into'>) => {
    const carried = ordered.filter((id) => carriedIds.includes(id));
    const rest = ordered.filter((id) => !carriedIds.includes(id));
    const at = rest.indexOf(targetId);
    if (at < 0) return ordered;
    rest.splice(position === 'before' ? at : at + 1, 0, ...carried);
    return rest;
};

/** Le righe portate: la presa, oppure la selezione se la presa ne fa parte — nell'ordine dato. */
export const carriedFrom = (ordered: string[], draggedId: string, selectedIds: string[]) =>
    selectedIds.includes(draggedId) ? ordered.filter((id) => selectedIds.includes(id)) : [draggedId];
